import { useState, useCallback, useMemo, useEffect } from 'react';
import { isCardRowEnabled } from '../../../utilities/card-utilities';
import isEmpty from 'utilities/isEmpty';

/**
 * Cards Selection Hook
 *
 * Manages card selection state and operations for the Cards page.
 * Handles single selection, multi-selection, select all, and selection queries.
 *
 * @param cardResults - Array of card data from Redux store
 * @returns Selection state and operations
 */

export interface SelectionState {
  [cardNumber: string]: {
    isSelected: boolean;
    cardNumber?: string; // Optional: used for convenience
    status: string;
    accountNumber: string;
    POSVerificationMethod: string;
  };
}

export interface CardSelectionOperations {
  selection: SelectionState;
  selectAll: boolean;
  selectedCards: any[];
  handleSelectionChange: (card: any) => void;
  triggerSelectAll: (isSelectAll: boolean) => void;
  resetSelection: () => void;
  getNumberOfSelectedRecords: () => number;
  getSelectedCards: () => string[];
  getSelectedCardAccounts: () => string[];
  getSelectedCardStatuses: () => string[];
  getSelectedCardPOSVerificationType: () => string[];
  isAllSelectedCardsInactive: () => boolean;
  canSelectCard: (card: any) => boolean;
}

export const useCardsSelection = (cardResults: any[]): CardSelectionOperations => {
  const [selection, setSelection] = useState<SelectionState>({});

  // Helper: Check if card can be selected
  const canSelectCard = useCallback((card: any): boolean => {
    return isCardRowEnabled.includes(card.status);
  }, []);

  // Derived state — computed from selection (react-useeffect skill: derived value, not useState + useEffect)
  const selectedCards = useMemo(() => Object.values(selection).filter((card) => card.isSelected), [selection]);

  // Derived state — selectAll is true when all selectable cards are selected
  const selectAll = useMemo(() => {
    const selectableCount = cardResults?.filter((cr) => canSelectCard(cr)).length ?? 0;
    const selectedCount = Object.values(selection).filter((x) => x.isSelected).length;
    return selectableCount > 0 && selectableCount === selectedCount;
  }, [cardResults, selection, canSelectCard]);

  // Handle individual card selection
  const handleSelectionChange = useCallback((card: any) => {
    const { cardNumber, parent: accountNumber, status } = card;
    setSelection((prev) => {
      const isPrevSelected = !!prev[cardNumber] && prev[cardNumber].isSelected;
      return {
        ...prev,
        [cardNumber]: {
          isSelected: !isPrevSelected,
          cardNumber,
          accountNumber,
          status,
          POSVerificationMethod: card.verifyPosPrompt,
        },
      };
    });
  }, []);

  // Handle select all / deselect all
  const triggerSelectAll = useCallback(
    (isSelectAll: boolean) => {
      const newSelection: SelectionState = {};

      if (isSelectAll) {
        cardResults.forEach((cr) => {
          newSelection[cr.cardNumber] = {
            isSelected: canSelectCard(cr),
            cardNumber: cr.cardNumber,
            accountNumber: cr.parent,
            status: cr.status,
            POSVerificationMethod: cr.verifyPosPrompt,
          };
        });
      }

      setSelection(newSelection);
    },
    [cardResults, canSelectCard]
  );

  // Reset selection state
  const resetSelection = useCallback(() => {
    setSelection({});
  }, []);

  // Query operations
  const getNumberOfSelectedRecords = useCallback((): number => {
    return Object.values(selection).filter((x) => x.isSelected).length;
  }, [selection]);

  const getSelectedCards = useCallback((): string[] => {
    return Object.entries(selection)
      .filter((x) => x[1].isSelected)
      .map((x) => x[0]);
  }, [selection]);

  const getSelectedCardAccounts = useCallback((): string[] => {
    return [
      ...new Set(
        Object.values(selection)
          .filter((x) => x.isSelected)
          .map((acc) => acc.accountNumber)
      ),
    ];
  }, [selection]);

  const getSelectedCardStatuses = useCallback((): string[] => {
    return [
      ...new Set(
        Object.values(selection)
          .filter((x) => x.isSelected)
          .map((x) => x.status)
      ),
    ];
  }, [selection]);

  const getSelectedCardPOSVerificationType = useCallback((): string[] => {
    return [
      ...new Set(
        Object.values(selection)
          .filter((x) => x.isSelected)
          .map((x) => x.POSVerificationMethod)
      ),
    ];
  }, [selection]);

  const isAllSelectedCardsInactive = useCallback((): boolean => {
    return !isEmpty(selectedCards) && selectedCards.every((selectedCard) => selectedCard.status === 'Inactive');
  }, [selectedCards]);

  return {
    selection,
    selectAll,
    selectedCards,
    handleSelectionChange,
    triggerSelectAll,
    resetSelection,
    getNumberOfSelectedRecords,
    getSelectedCards,
    getSelectedCardAccounts,
    getSelectedCardStatuses,
    getSelectedCardPOSVerificationType,
    isAllSelectedCardsInactive,
    canSelectCard,
  };
};
